import {
  installArrayAtPolyfill,
  installStructuredClonePolyfill,
} from "./polyfills";
installStructuredClonePolyfill();
installArrayAtPolyfill();
import "./pwaInstallPrompt";

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import App from "./App.tsx";
import { ChakraProvider } from "@chakra-ui/react";
import { HelmetProvider } from "react-helmet-async";
import system from "./chakra-config.ts";
import "react-datepicker/dist/react-datepicker.css";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "../react-big-calendar.css";
import "../react-datepicker.css";
import "@fontsource-variable/montserrat/index.css";
import { initSentry } from "./sentry";
import { initPostHog } from "./posthog";
import { reportWebVitals } from "./webVitals";
import { claimChunkReload, reloadForNewDeploy } from "./utils/chunkReload";
import { startPwaUpdateCoordinator } from "./utils/pwaUpdate";
import { recoverServiceWorkerRegistration } from "./utils/serviceWorkerRegistration";

initSentry();
initPostHog();
reportWebVitals();

// vite-plugin-pwa's auto-injected registerSW.js has no .catch(), so SW
// registration failures (ad blockers, in-app browsers, privacy modes,
// flaky mobile networks) surfaced as unhandled promise rejections in
// Sentry (HOMEHELPYWEBSITEFRONTEND-RC/RD). Register manually so failures
// are caught instead of thrown.
//
// The dynamic import itself can also fail — e.g. a stale client fetching
// a chunk hash a new deploy already removed (HOMEHELPYWEBSITEFRONTEND-RV,
// KAN-548) — so it needs its own .catch() alongside onRegisterError below.
//
// Both failures are also reported to Sentry (tagged `sw_registration_failed`),
// not just console.warn'd. A client whose registration fails while an OLDER
// service worker is still installed and controlling the page has no path to
// ever pick up a new deploy — it stays permanently stale. That population was
// previously invisible; this is how we size it.
import("virtual:pwa-register")
  .then(({ registerSW }) => {
    let registration: ServiceWorkerRegistration | undefined;
    // registerSW can invoke onNeedRefresh before it returns, so this cannot be initialized as const.
    // eslint-disable-next-line prefer-const
    let coordinator: ReturnType<typeof startPwaUpdateCoordinator> | undefined;
    let refreshArrivedBeforeCoordinator = false;

    const updateSW = registerSW({
      immediate: true,
      onNeedRefresh: () => {
        if (coordinator) coordinator.notifyUpdateAvailable();
        else refreshArrivedBeforeCoordinator = true;
      },
      onRegisteredSW: (_swUrl, nextRegistration) => {
        registration = nextRegistration;
      },
      onRegisterError: (error: unknown) => {
        console.warn("Service worker registration failed", error);

        // Workbox can fail with an opaque browser `TypeError: Internal error`. Native
        // registration often succeeds for the same worker, so only report an exception if that
        // recovery also fails. A recovered first failure is retained as a Sentry breadcrumb.
        void recoverServiceWorkerRegistration(error);
      },
    });

    coordinator = startPwaUpdateCoordinator({
      applyUpdate: () => updateSW(true),
      getRegistration: () => registration,
    });
    if (refreshArrivedBeforeCoordinator) coordinator.notifyUpdateAvailable();
  })
  .catch((error: unknown) => {
    console.warn("Failed to load service worker registration module", error);
    Sentry.captureException(error, {
      tags: { sw_registration_failed: "dynamic_import" },
    });
  });

// Eruda mobile DevTools — flip to true when you need in-browser debugging on mobile.
// Keep false in normal development and never commit as true
const ENABLE_ERUDA = false;
if (ENABLE_ERUDA) {
  const erudaScript = document.createElement("script");
  erudaScript.src = "https://cdn.jsdelivr.net/npm/eruda";
  erudaScript.onload = () =>
    (window as Window & { eruda?: { init: () => void } }).eruda?.init();
  document.head.appendChild(erudaScript);
}

// When a new deployment replaces chunk files, dynamic imports of old hashes 404.
// Reload to pick up the fresh index.html and correct chunk references. Shares the
// reload cooldown with lazyWithChunkReload in App.tsx to prevent double-reload.
//
// Vite rethrows the same error into the import() promise unless the event is
// cancelled, so preventDefault() when we take the reload — otherwise it also
// propagates to the ErrorBoundary and reports a crash we are already fixing.
window.addEventListener("vite:preloadError", (event) => {
  if (claimChunkReload()) {
    event.preventDefault();
    void reloadForNewDeploy();
  }
});

if ("scrollRestoration" in history) {
  history.scrollRestoration = "manual";
}
window.scrollTo(0, 0);

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <HelmetProvider>
      <ChakraProvider value={system}>
        <App />
      </ChakraProvider>
    </HelmetProvider>
  </StrictMode>
);
